// frontend/src/utils/pushNotifications.ts

import { getBaseApiUrl } from "../lib/env";

/**
 * Convertir une clé VAPID base64 URL-safe en Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Enregistrer le Service Worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    console.warn("⚠️ Service Worker non supporté");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register("/sw.js");
    console.log("✅ Service Worker enregistré");
    return registration;
  } catch (error) {
    console.error("❌ Erreur enregistrement SW:", error);
    return null;
  }
}

/**
 * S'abonner aux push notifications (appelé automatiquement après inscription)
 */
export async function subscribeToPushNotifications(
  clientId: string
): Promise<boolean> {
  try {
    console.log("🔔 Début de l'abonnement push pour client:", clientId);

    // 1. Vérifier le support
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.warn("⚠️ Push notifications non supportées");
      return false;
    }

    // 2. Demander la permission
    const permission = await Notification.requestPermission();
    console.log("🔔 Permission:", permission);

    if (permission !== "granted") {
      console.warn("⚠️ Permission refusée");
      return false;
    }

    // 3. Enregistrer le SW
    const registration = await registerServiceWorker();
    if (!registration) return false;

    await navigator.serviceWorker.ready;

    // 4. URL complète du backend
    const apiUrl = getBaseApiUrl();
    console.log("🌐 API URL:", apiUrl);

    // Si apiUrl se termine déjà par /api (comme notre nouvelle modif), on évite le doublon
    const vapidEndpoint = apiUrl.endsWith("/api") ? `${apiUrl}/push/vapid-key` : `${apiUrl}/api/push/vapid-key`;
    const subscribeEndpoint = apiUrl.endsWith("/api") ? `${apiUrl}/push/subscribe` : `${apiUrl}/api/push/subscribe`;

    // 5. Récupérer la clé VAPID
    console.log("🔑 Récupération de la clé VAPID depuis:", vapidEndpoint);
    const response = await fetch(vapidEndpoint);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ Erreur HTTP:", response.status, errorText);
      throw new Error(`Failed to get VAPID key: ${response.status}`);
    }

    const { publicKey } = await response.json();
    console.log("🔑 VAPID key reçue:", publicKey.substring(0, 20) + "...");

    // 6. ✅ CORRECTION : Convertir la clé et typer explicitement
    const applicationServerKey = urlBase64ToUint8Array(publicKey);

    // S'abonner aux push avec type correct
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    console.log("✅ Subscription créée");

    // 7. Envoyer au backend
    const subResponse = await fetch(subscribeEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        subscription: subscription.toJSON(),
      }),
    });

    if (!subResponse.ok) {
      const errorText = await subResponse.text();
      console.error("❌ Erreur subscription:", errorText);
      throw new Error(`Failed to subscribe: ${subResponse.status}`);
    }

    console.log(
      "✅ Push notifications activées automatiquement pour client:",
      clientId
    );
    return true;
  } catch (error) {
    console.error("❌ Erreur subscription:", error);
    return false;
  }
}

/**
 * Se désabonner des push notifications
 */
export async function unsubscribeFromPush(clientId: string): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return false;

    await subscription.unsubscribe();

    const apiUrl = getBaseApiUrl();
      
    const unsubscribeEndpoint = apiUrl.endsWith("/api") ? `${apiUrl}/push/unsubscribe` : `${apiUrl}/api/push/unsubscribe`;

    await fetch(unsubscribeEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientId }),
    });

    console.log("✅ Désabonné des push notifications");
    return true;
  } catch (error) {
    console.error("❌ Erreur désabonnement:", error);
    return false;
  }
}
